/**
 * ドロップダウンメニュー
 */

import { useEffect, useRef, useState } from 'react';

interface Option {
  value: string;
  label: string;
}

type DropdownMenuVariant = 'primary';

interface DropdownMenuProps {
  options: Option[];
  variant?: DropdownMenuVariant;
  onSelect: (option: Option) => void;
  defaultValue?: Option;
  placeholder?: string;
  menuWidth?: number;
  disabled?: boolean;
  containerClassName?: string;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  options,
  variant,
  onSelect,
  defaultValue,
  placeholder = '選択してください',
  menuWidth = 160,
  disabled = false,
  containerClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(defaultValue || null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedOption(defaultValue || null);
  }, [defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: Option) => {
    if (disabled) return;
    setSelectedOption(option);
    onSelect(option);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full ${containerClassName}`}
    >
      {/* ドロップダウンボタン */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex w-full items-center justify-between rounded-md border px-6 py-[10px] text-left text-sm 
          ${
            disabled
              ? 'cursor-not-allowed bg-gray-200 text-gray-500'
              : variant === 'primary'
                ? 'border-primary-500 text-primary-500'
                : 'bg-white'
          }`}
      >
        {selectedOption ? selectedOption.label : placeholder}
        <div
          className={`h-[5px] w-[10px] ${variant === 'primary' && !disabled ? 'bg-primary-500' : 'bg-black'}`}
          style={{
            clipPath: 'polygon(50% 100%, 0 10%, 100% 10%)',
          }}
        ></div>
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && !disabled && (
        <div
          className='absolute mt-1 overflow-hidden rounded-md border border-gray-200 bg-white text-sm'
          style={{
            width: `${menuWidth}px`,
          }}
        >
          <div className='max-h-[182px] overflow-y-auto'>
            {options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelect(option)}
                className='cursor-pointer px-6 py-[10px] transition-colors hover:bg-primary-500 hover:text-white'
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
